import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useOutlet } from 'react-router-dom'
import { observeForegroundPushMessages } from '@core/services/push-notifications/push-notifications-service'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import { syncPushNotificationsToken } from '@domain/features/push-notifications/push-notifications-feature'
import { useAnalyticsConsent } from '@presentation/hooks/useAnalyticsConsent'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useHistoryScrollRestoration } from '@presentation/hooks/useHistoryScrollRestoration'
import AuthenticationPage from '@presentation/screens/AuthenticationPage'
import UserProfilePage from '@presentation/screens/UserProfilePage'
import StructuredData from '@presentation/seo/StructuredData'
import {
  buildOrganizationSchema,
  buildWebsiteSchema,
} from '@presentation/seo/StructuredDataBuilders'
import Header from './Header'
import Footer from './Footer'

interface ActiveScreenSession {
  screenPath: string
  startedAtMs: number | null
}

function getKeepAliveRouteKey(pathname: string): string | null {
  if (pathname === '/') {
    return 'home'
  }
  if (pathname === '/news') {
    return 'news-list'
  }
  if (pathname === '/webinars' || pathname === '/webinars/latest') {
    return 'webinars-latest-list'
  }
  if (pathname === '/webinars/upcoming') {
    return 'webinars-upcoming-list'
  }
  if (pathname === '/hacks') {
    return 'hacks-list'
  }
  if (pathname === '/prof-bas-bloem-weekly') {
    return 'podcasts-list'
  }
  if (pathname === '/dr-michael-okun-my-take') {
    return 'instagram-reels-list'
  }

  return null
}

export default function Layout() {
  const location = useLocation()
  const outlet = useOutlet()
  useHistoryScrollRestoration()
  const keepAliveRouteKey = getKeepAliveRouteKey(location.pathname)
  const [cachedKeepAliveOutlets, setCachedKeepAliveOutlets] = useState<
    Record<string, ReturnType<typeof useOutlet>>
  >({})
  const { user, isAuthenticated } = useAuthUser()
  const isAnalyticsConsentGranted = useAnalyticsConsent()
  const searchParams = new URLSearchParams(location.search)
  const authMode = searchParams.get('authMode')
  const userProfile = searchParams.get('userProfile')
  const shouldRenderAuthModal = authMode === 'login' || authMode === 'signup' || authMode === 'reset'
  const shouldRenderUserProfileModal = userProfile === '1'
  const screenPath = useMemo(
    () => `${location.pathname}${location.search || ''}`,
    [location.pathname, location.search],
  )
  const siteStructuredData = useMemo(
    () => [buildOrganizationSchema(), buildWebsiteSchema()],
    [],
  )
  const activeScreenRef = useRef<ActiveScreenSession | null>(null)
  const isAuthenticatedRef = useRef(isAuthenticated)
  const isAnalyticsConsentGrantedRef = useRef(isAnalyticsConsentGranted)
  const syncedPushUserIdsRef = useRef<Set<string>>(new Set())
  const previousAuthenticatedUserIdRef = useRef<string | null>(null)
  const keepAliveOutlets = useMemo(() => {
    if (!keepAliveRouteKey || !outlet || cachedKeepAliveOutlets[keepAliveRouteKey]) {
      return cachedKeepAliveOutlets
    }

    return {
      ...cachedKeepAliveOutlets,
      [keepAliveRouteKey]: outlet,
    }
  }, [cachedKeepAliveOutlets, keepAliveRouteKey, outlet])

  useEffect(() => {
    if (!keepAliveRouteKey || !outlet || cachedKeepAliveOutlets[keepAliveRouteKey]) {
      return
    }

    setCachedKeepAliveOutlets((current) => ({
      ...current,
      [keepAliveRouteKey]: outlet,
    }))
  }, [cachedKeepAliveOutlets, keepAliveRouteKey, outlet])

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])

  useEffect(() => {
    isAnalyticsConsentGrantedRef.current = isAnalyticsConsentGranted
  }, [isAnalyticsConsentGranted])

  useEffect(() => {
    const flushActiveScreen = (now: number) => {
      const activeScreen = activeScreenRef.current
      if (!activeScreen || activeScreen.startedAtMs === null) {
        return
      }

      if (!isAnalyticsConsentGrantedRef.current) {
        activeScreen.startedAtMs = null
        return
      }

      const dwellSeconds = Math.max(0, (now - activeScreen.startedAtMs) / 1000)
      AnalyticsFeature.trackScreenTimeSpent(
        activeScreen.screenPath,
        dwellSeconds,
        isAuthenticatedRef.current,
      )
      activeScreen.startedAtMs = null
    }

    const now = Date.now()
    flushActiveScreen(now)

    if (!isAnalyticsConsentGranted) {
      activeScreenRef.current = {
        screenPath,
        startedAtMs: null,
      }
      return
    }

    activeScreenRef.current = {
      screenPath,
      startedAtMs: document.visibilityState === 'hidden' ? null : now,
    }

    AnalyticsFeature.trackScreenView(screenPath, isAuthenticatedRef.current)
  }, [isAnalyticsConsentGranted, screenPath])

  useEffect(() => {
    const flushActiveScreen = (now: number) => {
      const activeScreen = activeScreenRef.current
      if (!activeScreen || activeScreen.startedAtMs === null) {
        return
      }

      if (!isAnalyticsConsentGrantedRef.current) {
        activeScreen.startedAtMs = null
        return
      }

      const dwellSeconds = Math.max(0, (now - activeScreen.startedAtMs) / 1000)
      AnalyticsFeature.trackScreenTimeSpent(
        activeScreen.screenPath,
        dwellSeconds,
        isAuthenticatedRef.current,
      )
      activeScreen.startedAtMs = null
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushActiveScreen(Date.now())
        return
      }

      if (!isAnalyticsConsentGrantedRef.current) {
        return
      }

      const activeScreen = activeScreenRef.current
      if (activeScreen && activeScreen.startedAtMs === null) {
        activeScreen.startedAtMs = Date.now()
      }
    }

    const handlePageHide = () => {
      flushActiveScreen(Date.now())
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handlePageHide)
      flushActiveScreen(Date.now())
    }
  }, [])

  useEffect(() => {
    if (!shouldRenderAuthModal) {
      return
    }

    const modalName = `auth_${authMode ?? 'unknown'}`
    AnalyticsFeature.trackModalView(
      modalName,
      screenPath,
      isAuthenticatedRef.current,
    )
  }, [authMode, screenPath, shouldRenderAuthModal])

  useEffect(() => {
    if (!shouldRenderUserProfileModal) {
      return
    }

    AnalyticsFeature.trackModalView(
      'user_profile',
      screenPath,
      isAuthenticatedRef.current,
    )
  }, [screenPath, shouldRenderUserProfileModal])

  useEffect(() => {
    const userId = user?.uid
    if (!userId || !isAuthenticated) {
      return
    }

    if (syncedPushUserIdsRef.current.has(userId)) {
      return
    }

    syncedPushUserIdsRef.current.add(userId)
    void syncPushNotificationsToken(userId).catch((error) => {
      syncedPushUserIdsRef.current.delete(userId)
      console.error('Failed to sync web push token for authenticated user.', error)
    })
  }, [isAuthenticated, user?.uid])

  useEffect(() => {
    const currentUserId = user?.uid ?? null
    const previousUserId = previousAuthenticatedUserIdRef.current

    if (previousUserId && previousUserId !== currentUserId) {
      syncedPushUserIdsRef.current.delete(previousUserId)
    }

    if (!currentUserId) {
      syncedPushUserIdsRef.current.clear()
    }

    previousAuthenticatedUserIdRef.current = currentUserId
  }, [user?.uid])

  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      return
    }

    console.log('observeForegroundPushMessages')
    const unsubscribe = observeForegroundPushMessages((payload) => {
      console.log('[push][foreground] message appeared', payload)
    })

    return unsubscribe
  }, [isAuthenticated, user?.uid])

  return (
    <div className="flex min-h-screen flex-col">
      <StructuredData data={siteStructuredData} />
      <Header />
      <main className="flex-1">
        {Object.entries(keepAliveOutlets).map(([routeKey, cachedOutlet]) => (
          <div
            key={routeKey}
            aria-hidden={routeKey !== keepAliveRouteKey}
            className={routeKey === keepAliveRouteKey ? '' : 'hidden'}
          >
            {cachedOutlet}
          </div>
        ))}
        {!keepAliveRouteKey ? outlet : null}
      </main>
      <Footer />
      {shouldRenderAuthModal ? <AuthenticationPage /> : null}
      {shouldRenderUserProfileModal ? <UserProfilePage /> : null}
    </div>
  )
}
